import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, inject } from '@angular/core';
import { AbstractControl, FormBuilder, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { Location } from '../location/location.model';
import { LocationService } from '../location/location.service';
import { LazyLocationPickerDirective } from '../shared/location-picker/lazy-location-picker.directive';
import { FieldWorkPackage } from './field-work.models';

export interface FieldWorkLocationOption extends Partial<Location> {
  id: number;
  name: string;
  fieldWorkLabel?: string;
  stopType?: string;
  stopNumber?: number;
}

@Component({
  selector: 'app-field-work-package-create-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    DialogModule,
    ButtonModule,
    DatePickerModule,
    InputTextModule,
    SelectModule,
    IftaLabelModule,
    InputGroupModule,
    InputGroupAddonModule,
    LazyLocationPickerDirective
  ],
  templateUrl: './field-work-package-create-dialog.component.html',
  styleUrls: ['./field-work-package-create-dialog.component.css']
})
export class FieldWorkPackageCreateDialogComponent implements OnInit, OnChanges {
  private readonly fb = inject(FormBuilder);
  private readonly locationService = inject(LocationService);

  @Input() visible = false;
  @Input() value: FieldWorkPackage = {};
  @Input() saving = false;
  @Input() error = '';
  /** When supplied, the dialog is restricted to this operationally scoped list. */
  @Input() locationOptions?: FieldWorkLocationOption[];

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() submitted = new EventEmitter<FieldWorkPackage>();

  readonly activityOptions = [
    'GENERAL', 'LOADING', 'UNLOADING', 'DELIVERY', 'SITE_SUPPORT', 'INSTALLATION',
    'REMOVAL', 'RETURN', 'HANDLING', 'INSPECTION', 'STUFFING', 'DESTUFFING',
    'RECEIVING', 'PICKING', 'PACKING', 'PUTAWAY', 'DOCUMENTATION', 'CLEARANCE_SUPPORT'
  ].map(value => ({ label: value.replaceAll('_', ' '), value }));
  readonly yesNo = [{ label: 'Yes', value: true }, { label: 'No', value: false }];

  locations: FieldWorkLocationOption[] = [];
  submittedOnce = false;

  readonly form = this.fb.group({
    title: ['', Validators.required],
    activityType: ['GENERAL', Validators.required],
    plannedStart: [null as Date | null, Validators.required],
    plannedEnd: [null as Date | null, Validators.required],
    locationId: [null as number | null, Validators.required],
    billable: [false, Validators.required]
  }, { validators: this.validSchedule() });

  ngOnInit(): void {
    this.refreshLocationSource();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['locationOptions']) {
      this.refreshLocationSource();
    }
    if (changes['value'] || changes['visible']?.currentValue === true) {
      this.reset();
    }
  }

  get targetLabel(): string {
    return this.value.targetReference || (this.value.targetId ? `${this.value.targetType} #${this.value.targetId}` : 'Current record');
  }

  get editing(): boolean {
    return Boolean(this.value.fieldWorkPackageId);
  }

  get dialogTitle(): string {
    return this.editing ? 'Edit Work Package' : 'Add Work Package';
  }

  get dialogDescription(): string {
    return this.editing
      ? 'Update the operational activity before it is released for execution.'
      : 'Add the people and reusable equipment plan after the operational record has been created.';
  }

  get submitLabel(): string {
    return this.editing ? 'Save Changes' : 'Create Work Package';
  }

  get submitIcon(): string {
    return this.editing ? 'pi pi-save' : 'pi pi-plus';
  }

  get selectedActivityLabel(): string {
    return `${this.form.get('activityType')?.value || 'GENERAL'}`.replaceAll('_', ' ');
  }

  get selectedLocationLabel(): string {
    const locationId = this.form.get('locationId')?.value;
    const location = this.locations.find(candidate => candidate.id === locationId);
    return location ? this.locationLabel(location) : (this.value.locationName || 'Select location');
  }

  get minimumEndDate(): Date | undefined {
    return this.form.get('plannedStart')?.value ?? undefined;
  }

  reset(): void {
    this.submittedOnce = false;
    const requestedLocationId = this.value.locationId ?? null;
    const locationId = this.locationOptions === undefined
      || requestedLocationId === null
      || this.locations.some(location => location.id === requestedLocationId)
      ? requestedLocationId
      : null;
    this.form.reset({
      title: this.value.title || '',
      activityType: this.value.activityType || 'GENERAL',
      plannedStart: this.dateTimeValue(this.value.plannedStart),
      plannedEnd: this.dateTimeValue(this.value.plannedEnd),
      locationId,
      billable: this.value.billable ?? false
    });
  }

  close(): void {
    if (!this.saving) {
      this.visibleChange.emit(false);
    }
  }

  submit(): void {
    this.submittedOnce = true;
    if (this.form.invalid || this.saving) {
      this.form.markAllAsTouched();
      return;
    }

    const formValue = this.form.getRawValue();
    const location = this.locations.find(candidate => candidate.id === formValue.locationId);
    this.submitted.emit({
      ...this.value,
      title: formValue.title?.trim(),
      activityType: formValue.activityType || 'GENERAL',
      plannedStart: this.localDateTimePayload(formValue.plannedStart),
      plannedEnd: this.localDateTimePayload(formValue.plannedEnd),
      locationId: formValue.locationId ?? undefined,
      locationName: location?.name || this.value.locationName,
      billable: formValue.billable ?? false
    });
  }

  locationLabel(location?: FieldWorkLocationOption): string {
    if (!location) return '';
    if (location.fieldWorkLabel) return location.fieldWorkLabel;
    const secondary = location.externalLocationReference || location.shortDescription;
    return [location.name, secondary].filter(Boolean).join(' - ');
  }

  private refreshLocationSource(): void {
    if (this.locationOptions !== undefined) {
      this.locations = [...this.locationOptions];
      const selectedId = this.form.get('locationId')?.value;
      if (selectedId && !this.locations.some(location => location.id === selectedId)) {
        this.form.get('locationId')?.setValue(null);
      }
      return;
    }
    this.locations = [];
    this.ensureInitialLocationLoaded();
  }

  private ensureInitialLocationLoaded(): void {
    if (this.locationOptions !== undefined) return;
    const locationId = this.value.locationId;
    if (!locationId || this.locations.some(location => location.id === locationId)) return;
    this.locationService.getLocationById(locationId).subscribe({
      next: response => {
        const location = response.data?.[0];
        if (location && !this.locations.some(candidate => candidate.id === location.id)) {
          this.locations = [location, ...this.locations];
        }
      }
    });
  }

  onLazyLocationSelected(locationId: number | null): void {
    if (!locationId || this.locationOptions !== undefined
      || this.locations.some(location => location.id === locationId)) {
      return;
    }
    this.locationService.getLocationById(locationId).subscribe({
      next: response => {
        const location = response.data?.[0];
        if (location && !this.locations.some(candidate => candidate.id === location.id)) {
          this.locations = [location, ...this.locations];
        }
      }
    });
  }

  private dateTimeValue(value?: string | Date): Date | null {
    if (!value) return null;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  private localDateTimePayload(value?: Date | null): string | undefined {
    if (!value) return undefined;
    const pad = (part: number): string => `${part}`.padStart(2, '0');
    return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`
      + `T${pad(value.getHours())}:${pad(value.getMinutes())}:00`;
  }

  private validSchedule(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const start = control.get('plannedStart')?.value;
      const end = control.get('plannedEnd')?.value;
      if (!start || !end) return null;
      return new Date(end).getTime() > new Date(start).getTime() ? null : { invalidSchedule: true };
    };
  }
}
